import React, { useCallback } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import { Input } from '../../../components/common-inputs';
import RegenerateButton from './RegenerateButton';
import WithError from '../../../components/with-error/WithError';
import passwordGenerator from '../../../utils/passwordGeneration';
import ActionsSection from '../actions-sections/ActionsSection';
import CopyButton from '../../../components/copy-button/CopyButton';

import scss from './ShareSection.module.scss';

const SharePasswordForm = ({ onSubmit, initialValues, isPasswordFormDisabled }) => {
  const { t } = useTranslation('projects');
  const { reset, change } = useForm();
  const { dirty, values, valid } = useFormState();

  const isShareSectionLoading = useSelector((state) => state.projects.isShareSectionLoading);

  const handleDiscardsChanges = useCallback(() => {
    reset(initialValues);
  }, [reset, initialValues]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(values.link);
  }, [values]);

  const handleCopyPassword = useCallback(() => {
    navigator.clipboard.writeText(values.password);
  }, [values]);

  const handleGeneratePassword = useCallback(() => {
    change('password', passwordGenerator({ length: 20, includeSpecialSymbols: false }));
  }, [change]);

  return (
    <div className={scss.passwordForm}>
      <div className={scss.passwordFormFields}>
        <Field name='link'>
          {({ input }) => (
            <WithLabel
              variant={LABEL_STYLES.CUSTOM}
              extraClassName={scss.formRow}
              label={t('link')}
              isBold
              maxWidth={520}>
              <div className={scss.formRowInput}>
                <Input
                  type='text'
                  size='sm'
                  formProps={input}
                  isReadOnly
                  maxWidth={260}
                  disabled={isPasswordFormDisabled || isShareSectionLoading}
                />
                <CopyButton onClick={handleCopyLink} disabled={isPasswordFormDisabled || isShareSectionLoading} />
                <div></div>
              </div>
            </WithLabel>
          )}
        </Field>

        <Field name='password'>
          {({ input, meta }) => (
            <WithLabel
              variant={LABEL_STYLES.CUSTOM}
              extraClassName={scss.formRow}
              label={t('password')}
              isBold
              maxWidth={520}>
              <div className={scss.formRowInput}>
                <WithError
                  isShowError={meta.touched && meta.error}
                  errorText={t(meta.error)}
                  isAbsolute
                  extraClassName={scss.passwordInput}>
                  <Input
                    type='text'
                    size='sm'
                    formProps={input}
                    disabled={isPasswordFormDisabled || isShareSectionLoading}
                    isError={meta.touched && meta.error}
                  />
                </WithError>
                <CopyButton onClick={handleCopyPassword} disabled={isPasswordFormDisabled || isShareSectionLoading} />
                <RegenerateButton
                  onClick={handleGeneratePassword}
                  disabled={isPasswordFormDisabled || isShareSectionLoading}
                />
              </div>
            </WithLabel>
          )}
        </Field>
      </div>

      <ActionsSection
        dirty={dirty}
        valid={valid}
        disableDiscard={isPasswordFormDisabled}
        disableSubmit={isPasswordFormDisabled}
        onDiscard={handleDiscardsChanges}
        onSubmit={onSubmit}
        isLoading={isShareSectionLoading}
      />
    </div>
  );
};

export default SharePasswordForm;
